import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import baseStyles from './css/BloodPressure.module.css';
import styles from './css/WoundCare.module.css';
import { Bandage, Send } from 'lucide-react';

const WoundCare = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    const [woundType, setWoundType] = useState('');
    const [woundLocation, setWoundLocation] = useState('');
    const [duration, setDuration] = useState('');

    const [infectionSigns, setInfectionSigns] = useState({
        redness: false,
        swelling: false,
        warmth: false,
        pusDischarge: false,
    });
    const [tetanusUpToDate, setTetanusUpToDate] = useState(false);

    const [dressingApplied, setDressingApplied] = useState('');
    const [clinicalNotes, setClinicalNotes] = useState('');

    const handleInfectionSignChange = (key) => {
        setInfectionSigns((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                wound_care: {
                    woundType,
                    woundLocation,
                    duration,
                    infectionSigns,
                    tetanusUpToDate,
                    dressingApplied,
                    clinicalNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(baseStyles.hideSidebar);
        return () => {
            document.body.classList.remove(baseStyles.hideSidebar);
        };
    }, []);

    return (
        <div className={baseStyles.bloodPressureContainer}>
            <Navbar />

            <main className={baseStyles.mainContent}>
                <div className={baseStyles.card}>
                    <div className={baseStyles.cardHeader}>
                        <div className={baseStyles.headerIcon} aria-hidden="true">
                            <Bandage />
                        </div>
                        <h1 className={styles.title}>Wound Care</h1>
                    </div>

                    <div className={baseStyles.patientCard}>
                        <div className={baseStyles.avatar} aria-hidden="true">{initials}</div>
                        <div className={baseStyles.patientText}>
                            <div className={baseStyles.patientName}>{patientName}</div>
                            <div className={baseStyles.patientMetaRow}>
                                <span className={baseStyles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={baseStyles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>WOUND DETAILS</div>

                        <div className={styles.detailsRow}>
                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Wound Type</label>
                                <Dropdown
                                    name="woundType"
                                    value={woundType}
                                    onChange={setWoundType}
                                    options={['Laceration', 'Abrasion', 'Puncture', 'Burn', 'Ulcer', 'Other']}
                                    placeholder="Select..."
                                    className={styles.readingInput}
                                    alignLeft
                                />
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Location</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="text"
                                        value={woundLocation}
                                        onChange={(e) => setWoundLocation(e.target.value)}
                                        className={baseStyles.readingInput}
                                        placeholder="e.g., Left forearm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.durationBlock}>
                            <div className={styles.durationLabel}>Duration</div>
                            <div className={styles.durationButtons}>
                                <button
                                    type="button"
                                    className={`${styles.durationButton} ${duration === '<10' ? styles.active : ''}`}
                                    onClick={() => setDuration('<10')}
                                >
                                    &lt; 10 min
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.durationButton} ${duration === '>10' ? styles.active : ''}`}
                                    onClick={() => setDuration('>10')}
                                >
                                    &gt; 10 min
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>INFECTION SIGNS</div>

                        <div className={baseStyles.symptomsList}>
                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="redness"
                                    checked={infectionSigns.redness}
                                    onChange={() => handleInfectionSignChange('redness')}
                                />
                                <label htmlFor="redness" className={baseStyles.checkboxLabel}>Redness</label>
                            </div>

                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="swelling"
                                    checked={infectionSigns.swelling}
                                    onChange={() => handleInfectionSignChange('swelling')}
                                />
                                <label htmlFor="swelling" className={baseStyles.checkboxLabel}>Swelling</label>
                            </div>

                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="warmth"
                                    checked={infectionSigns.warmth}
                                    onChange={() => handleInfectionSignChange('warmth')}
                                />
                                <label htmlFor="warmth" className={baseStyles.checkboxLabel}>Warmth</label>
                            </div>

                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="pusDischarge"
                                    checked={infectionSigns.pusDischarge}
                                    onChange={() => handleInfectionSignChange('pusDischarge')}
                                />
                                <label htmlFor="pusDischarge" className={baseStyles.checkboxLabel}>Pus/discharge</label>
                            </div>
                        </div>

                        <div className={baseStyles.toggleSection}>
                            <label className={baseStyles.toggleLabel}>Tetanus status up to date</label>
                            <button
                                type="button"
                                className={`${baseStyles.toggleButton} ${tetanusUpToDate ? baseStyles.active : ''}`}
                                onClick={() => setTetanusUpToDate((v) => !v)}
                                aria-pressed={tetanusUpToDate}
                            >
                                <div className={baseStyles.toggleSlider}></div>
                            </button>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>TREATMENT</div>

                        <div className={baseStyles.readingItem}>
                            <label className={baseStyles.readingLabel}>Dressing Applied</label>
                            <div className={baseStyles.inputGroup}>
                                <input
                                    type="text"
                                    value={dressingApplied}
                                    onChange={(e) => setDressingApplied(e.target.value)}
                                    className={baseStyles.readingInput}
                                    placeholder="e.g., Sterile gauze + adhesive"
                                />
                            </div>
                        </div>

                        <br></br>

                        <div className={baseStyles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={baseStyles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={baseStyles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={baseStyles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default WoundCare;